import { FileText, ShoppingCart } from "lucide-react";
import Button from "@/store/components/Button";
import StatsGrid from "@/store/components/StatsGrid";
import CouponCategoryChips from "@/store/components/CouponCategoryChips";
import ExpiryWarning from "@/store/components/ExpiryWarning";
import { CART_URL, signInUrl } from "@/store/constants";
import type { Coupon, CouponStats } from "@/store/types";

/** White pill "View Cart" button, orange text/icon — floats above the hero gradient via its own drop shadow rather than a solid fill color. */
export function CartButton() {
  return (
    <Button
      variant="primary"
      className="agd:flex-shrink-0 agd:inline-flex agd:items-center agd:gap-[9px] agd:rounded-full agd:border-0 agd:bg-white agd:px-[30px] agd:py-[14px] agd:text-sm agd:font-[Nunito_Sans] agd:font-extrabold agd:text-accent agd:shadow-[0_6px_20px_rgba(0,0,0,0.18)] hover:not-disabled:agd:opacity-90 agd:max-md:gap-[7px] agd:max-md:px-5 agd:max-md:py-2.5 agd:max-md:text-[12.5px] agd:max-md:shadow-[0_5px_16px_rgba(0,0,0,0.18)]"
      onClick={() => {
        window.location.href = location.origin + CART_URL;
      }}
    >
      <ShoppingCart size={16} /> View Cart
    </Button>
  );
}

/** "Back to store" link that drops the category/product URL filters. */
export function BackLink() {
  return (
    <a
      href={location.pathname}
      className="agd:mb-3.5 agd:inline-block agd:text-[0.85rem] agd:font-semibold agd:text-white agd:no-underline hover:agd:underline"
    >
      &larr; Back to store
    </a>
  );
}

/** The "AGD Store" wordmark, white-on-gradient for the hero band. */
function Wordmark() {
  return (
    <h1 className="agd:m-0 agd:text-[27px] agd:font-extrabold agd:text-white agd:max-md:text-[19px]">
      AGD Store
    </h1>
  );
}

/** Subtitle shown under the wordmark. */
function Subtitle() {
  return (
    <p className="agd:m-0 agd:mt-1 agd:text-[14.5px] agd:leading-[1.4] agd:text-white/85 agd:max-md:text-[12.5px]">
      Purchase continuing education products using your available coupon credits.
    </p>
  );
}

export const HERO_SHELL =
  "agd:relative agd:rounded-xl agd:px-8 agd:pt-7 agd:pb-10 agd:text-white agd:shadow-md agd:max-md:px-4 agd:max-md:pt-5 agd:max-md:pb-[34px]";
export const HERO_GRADIENT = {
  background: "linear-gradient(120deg, #0090CE 0%, #00A2E5 45%, #00A2E5 85%, #FF8300 100%)",
};

/** Slim header for the single-product detail view. */
export function SlimHeader() {
  return (
    <div className={HERO_SHELL + " agd:mb-12"} style={HERO_GRADIENT}>
      <BackLink />
      <div className="agd:flex agd:flex-wrap agd:items-start agd:justify-between agd:gap-3 agd:max-md:flex-col">
        <div>
          <Wordmark />
          <Subtitle />
        </div>
        <CartButton />
      </div>
    </div>
  );
}

/** "Sign in to see your coupons" nudge shown to anonymous visitors in place of stats/chips. */
function SignInNudge() {
  return (
    <div className="agd:mt-6 agd:flex agd:flex-wrap agd:items-center agd:justify-between agd:gap-4 agd:rounded-xl agd:bg-card agd:px-6 agd:py-5 agd:shadow-md">
      <div>
        <div className="agd:text-[0.95rem] agd:font-semibold agd:text-foreground">
          Sign in to see your available coupons
        </div>
        <div className="agd:mt-1 agd:text-[0.82rem] agd:text-muted-foreground">
          You can browse and search the catalog without signing in.
        </div>
      </div>
      <a href={signInUrl()}>
        <Button variant="primary" size="sm">
          Sign In
        </Button>
      </a>
    </div>
  );
}

interface StoreHeaderProps {
  bundlesPurchased: number;
  stats: CouponStats;
  coupons: Coupon[];
  activeCoupon: Coupon | null;
  isFiltered: boolean;
  isLoggedIn: boolean;
  onOpenHistory: () => void;
}

/** Full store header: hero band, then stats/coupon chips (or a sign-in nudge) and history button. */
export default function StoreHeader({
  bundlesPurchased,
  stats,
  coupons,
  activeCoupon,
  isFiltered,
  isLoggedIn,
  onOpenHistory,
}: StoreHeaderProps) {
  return (
    <div className="agd:mb-8 agd:max-md:mb-5">
      <div className={HERO_SHELL} style={HERO_GRADIENT}>
        {isFiltered ? <BackLink /> : null}
        <div className="agd:flex agd:flex-wrap agd:items-start agd:justify-between agd:gap-3 agd:max-md:flex-col">
          <div>
            <Wordmark />
            <Subtitle />
          </div>
          <CartButton />
        </div>
      </div>

      <div className="agd:relative agd:z-10 agd:-mt-8 agd:px-2 agd:max-md:-mt-[22px] agd:max-md:px-0">
        {isLoggedIn ? (
          <>
            <StatsGrid bundlesPurchased={bundlesPurchased} stats={stats} />
            <CouponCategoryChips coupons={coupons} />
            <ExpiryWarning coupon={activeCoupon} />
            <Button
              variant="outline"
              size="sm"
              className="agd:mt-4 agd:max-md:mt-3"
              onClick={onOpenHistory}
            >
              <FileText size={16} /> View purchase history
            </Button>
          </>
        ) : (
          <SignInNudge />
        )}
      </div>
    </div>
  );
}
